import { Injectable, ElementRef, NgZone, inject } from '@angular/core';
import {
  EventSessionPlanLetterResponseDto,
  SalesPlanSeatResponseDto,
  SalesSeatCategoryResponseDto,
  SalesSeatStatusEnumResponseDto,
  SalesSeatTypeEnumResponseDto,
  SeatsBlockGroupResponseDto,
} from '@core/services/apiclient';
import { ChartsUtils } from '@shared/components/charts/charts.utils';
import Konva from 'konva';
import { RectConfig } from 'konva/lib/shapes/Rect';
import { Vector2d } from 'konva/lib/types';
import * as _ from 'underscore';
import { CanvasProps, EventSeatSelection } from '../models/sales-plan.model';
import { polygonContains } from 'd3';
import { clone } from 'underscore';
import { SalesPlanBlockViewMinimapService } from './sales-plan-blockview-minimap.service';
import { BlockedSeatIcon, SearchResultIcon, SeatsPerGroup } from '../models/sales-plan-constants';
import { BehaviorSubject } from 'rxjs';
import { SalesBlockViewData } from '@core/models/sales-plan.model';
import { PlanDrawingService } from './plan-drawing.service';

@Injectable({
  providedIn: 'root',
})
export class SalesPlanEventBlockViewUtilsService {
  private readonly minimap = inject(SalesPlanBlockViewMinimapService);
  private readonly ngZone = inject(NgZone);
  private readonly planDrawingService = inject(PlanDrawingService);

  public eventData: SalesBlockViewData;
  // these object are mandatory for performance sake
  private seatNodeMap: { [key: string]: Konva.Group };
  private notFreeSeatNodeMap: { [key: string]: Konva.Group };
  private parentGroupMap: { [key: string]: Konva.Group };
  private iconsMap: { blockedSeatImage?: HTMLImageElement; [key: string]: HTMLImageElement };
  private seatSize: number;
  private shapesMap: {
    rect: Konva.Rect;
    circle: Konva.Rect;
    number: Konva.Label;
    selectionHighlighter: Konva.Rect;
  };

  private loaderInfo$ = new BehaviorSubject<{ show: boolean; progress?: number; description?: string }>({
    show: false,
    progress: 0,
    description: '',
  });

  /**
   * initialize some variables that our service needs
   */
  public async init(data: SalesBlockViewData, seatSize: number) {
    this.eventData = clone(data);
    this.seatNodeMap = {};
    this.notFreeSeatNodeMap = {};
    this.parentGroupMap = {};
    this.iconsMap = {};
    this.seatSize = seatSize;
    await this.prepareShapeCache();
  }

  /**
   * seat node map is an object that contains all the seat canvas nodes. Useful in the search
   */
  private pushToSeatNodeMap(physicalSeatId: number, node: Konva.Group): void {
    this.seatNodeMap[physicalSeatId] = node;
  }

  private pushToNotFreeSeatNodeMap(physicalSeatId: number, node: Konva.Group): void {
    this.notFreeSeatNodeMap[physicalSeatId] = node;
  }

  /**
   * create the group that contains the seats chunk. This parent group will be cached to improve performance
   */
  public createSeatParentGroup(
    seats: SalesPlanSeatResponseDto[],
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
    categoryDictionary: Map<string, Konva.Group>,
    contingentDictionary: Map<string, Konva.Group>,
    categoryLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    this.planDrawingService.updatePlanDrawingStatus(false);
    const parentGroup = new Konva.Group({
      id: `sales-parent-seat-group`,
    });
    this.batchDrawSeatsProcess(
      seats,
      scaleX,
      scaleY,

      ({ seat, scaleX, scaleY }) => {
        const seatGroup = this.createEventSeatGroup(
          {
            seat,
            scaleX,
            scaleY,
          },
          categoryDictionary,
          contingentDictionary,
          categoryLayer,
          contingentLayer,
        );
        parentGroup.add(seatGroup);
      },
    ).then(() => {
      this.loaderInfo$.next({
        show: false,
        progress: 100,
        description: 'salesPlan.planLoaded',
      });
      parentGroup.setAttrs({
        width: parentGroup.children?.[parentGroup.children?.length - 1]?.x() - parentGroup.children?.[0]?.x(),
        height:
          parentGroup.children?.[parentGroup.children?.length - 1]?.y() - parentGroup.children?.[0]?.y(),
      });
      this.planDrawingService.updatePlanDrawingStatus(true);
    });
    return parentGroup;
  }

  private batchDrawSeatsProcess(
    seats: SalesPlanSeatResponseDto[],
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
    drawSeatInChunks: (options: {
      seat: SalesPlanSeatResponseDto;
      scaleX: (position: number) => number;
      scaleY: (position: number) => number;
    }) => void,
  ): Promise<void[]> {
    if (typeof Worker !== 'undefined') {
      const batchSize = SeatsPerGroup;
      const numberOfSeats = seats.length;
      const numberOfWorkers = Math.ceil(numberOfSeats / batchSize);
      const promises: Promise<any>[] = [];
      let completedWorkers = 0;
      for (let i = 0; i < numberOfWorkers; i++) {
        const start = i * batchSize;
        const end = Math.min((i + 1) * batchSize, numberOfSeats);
        const partitionOfArray = seats.slice(start, end);

        const workerPromise = new Promise<void>((resolve, reject) => {
          const worker = new Worker(new URL('./../sales-plan.worker', import.meta.url));

          worker.onmessage = ({ data }: MessageEvent<SalesPlanSeatResponseDto[]>) => {
            this.ngZone.run(() => {
              data.forEach((seat) => {
                drawSeatInChunks({
                  seat,
                  scaleX,
                  scaleY,
                });
              });
              completedWorkers++;
              resolve();
            });
            this.updateProgress(completedWorkers, numberOfWorkers);
          };
          worker.onerror = reject;
          worker.postMessage(partitionOfArray);
        });
        promises.push(workerPromise);
      }
      return Promise.all(promises);
    } else {
      seats.forEach((seat) => {
        drawSeatInChunks({
          seat,
          scaleX,
          scaleY,
        });
      });
      return Promise.resolve([]);
    }
  }

  updateProgress(completedWorkers: number, totalWorkers: number) {
    const progress = Math.floor((completedWorkers / totalWorkers) * 100);
    this.loaderInfo$.next({
      show: true,
      progress: progress,
      description: progress === 0 ? 'salesPlan.initialPlanLoading' : 'salesPlan.planSeatLoadingProgress',
    });
  }

  public progressInfo() {
    return this.loaderInfo$.asObservable();
  }

  /**
   * creates the group that contains all the seat elements: seat box, seat number, selection highlighter
   */
  public createEventSeatGroup(
    options: {
      seat: SalesPlanSeatResponseDto;
      scaleX: (position: number) => number;
      scaleY: (position: number) => number;
    },
    categoryDictionary: Map<string, Konva.Group>,
    contingentDictionary: Map<string, Konva.Group>,
    categoryLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    const seatGroup = this.initializeGroupNode(
      options.seat,
      options.scaleX(options.seat?.positionX),
      options.scaleY(options.seat?.positionY),
    );
    this.appendIconToSeat(options.seat, seatGroup);
    this.initializeSeatInDictionaries(
      options.seat,
      seatGroup,
      categoryDictionary,
      contingentDictionary,
      categoryLayer,
      contingentLayer,
    );

    const seatBox = this.drawSeatBox(options.seat);
    seatGroup.add(seatBox);

    if (this.eventData?.blockId) {
      this.minimap.addMinimapElement(seatBox, seatGroup?.x(), seatGroup?.y());
    }
    seatGroup.add(this.drawSeatNumber(options.seat));
    if (!this.isSeatSelectable(options.seat)) {
      this.appendIconToSeat(options.seat, seatGroup);
    }
    if (!this.isSeatSelectable(options.seat)) {
      this.pushToNotFreeSeatNodeMap(options.seat.physicalSeatId, seatGroup);
    }
    return seatGroup;
  }

  initializeSeatInDictionaries(
    seat: SalesPlanSeatResponseDto,
    seatGroup: Konva.Group,
    categoryDictionary: Map<string, Konva.Group>,
    contingentDictionary: Map<string, Konva.Group>,
    categoryLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    const processGroup = (
      groupKey: string,
      dictionary: Map<string, Konva.Group>,
      layer: Konva.Layer,
      configureSeat: (clonedSeatGroup: Konva.Group) => void,
    ) => {
      let group = dictionary.get(groupKey);
      if (!group) {
        group = new Konva.Group({ id: groupKey });
        dictionary.set(groupKey, group);
        layer.add(group);
      }

      const clonedSeatGroup = seatGroup.clone();
      configureSeat(clonedSeatGroup);
      group.add(clonedSeatGroup);
      layer.batchDraw();
    };

    if (this.isSeatSelectable(seat)) {
      const categoryId = seat?.seatCategory?.id;
      if (categoryId !== undefined && categoryId !== null) {
        processGroup(
          `sales-seat-category-${categoryId}`,
          categoryDictionary,
          categoryLayer,
          (clonedSeatGroup) => {
            const seatBox = this.getShapeConfig(seat, true);
            clonedSeatGroup.add(seatBox);
          },
        );
      }
      const contingentId = seat?.seatContingent?.id;
      if (contingentId !== undefined && contingentId !== null) {
        processGroup(
          `sales-seat-contingent-${contingentId}`,
          contingentDictionary,
          contingentLayer,
          (clonedSeatGroup) => {
            clonedSeatGroup.add(this.drawSeatHighlighter(seat));
            const seatBox = this.getShapeConfig(seat, false);
            clonedSeatGroup.add(seatBox);
            this.pushToSeatNodeMap(seat.physicalSeatId, clonedSeatGroup);
          },
        );
      }
    }
  }

  createNewSeatInDictionaries(
    options: {
      seat: SalesPlanSeatResponseDto;
      scaleX: (position: number) => number;
      scaleY: (position: number) => number;
    },
    categoryDictionary: Map<string, Konva.Group>,
    contingentDictionary: Map<string, Konva.Group>,
    categoryLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    const seatGroup = this.initializeGroupNode(
      options.seat,
      options.scaleX(options.seat?.positionX),
      options.scaleY(options.seat?.positionY),
    );

    this.appendIconToSeat(options.seat, seatGroup);

    this.initializeSeatInDictionaries(
      options.seat,
      seatGroup,
      categoryDictionary,
      contingentDictionary,
      categoryLayer,
      contingentLayer,
    );
    if (!this.isSeatSelectable(options.seat)) {
      this.appendIconToSeat(options.seat, seatGroup);
    }
  }

  initializeGroupNode(seat: SalesPlanSeatResponseDto, scaleX: number, scaleY: number) {
    const seatGroupNode = new Konva.Group({
      width: this.seatSize,
      height: this.seatSize,
      id: `sales-seat-group-${seat?.physicalSeatId}`,
      x: scaleX,
      y: scaleY,
      rotation: seat?.rotation,
    });
    return seatGroupNode;
  }

  getShapeConfig(seat: SalesPlanSeatResponseDto, isCategoryShape: boolean) {
    switch (seat?.typeSeat) {
      case SalesSeatTypeEnumResponseDto.Normal:
        return this.shapesMap?.rect.clone({
          id: `sales-seat-box-${seat?.physicalSeatId}`,
          name: `sales-seat-box-${seat?.physicalSeatId}`,
          ...(isCategoryShape
            ? this.getCategorySeatShapeColor(seat)
            : this.getContingentSeatShapeColor(seat)),
        });
      case SalesSeatTypeEnumResponseDto.FoldingSeat:
        return this.shapesMap?.circle.clone({
          id: `sales-seat-box-${seat?.physicalSeatId}`,
          name: `sales-seat-box-${seat?.physicalSeatId}`,
          ...(isCategoryShape
            ? this.getCategorySeatShapeColor(seat)
            : this.getContingentSeatShapeColor(seat)),
        });
    }
  }

  getContingentSeatShapeColor(seat: SalesPlanSeatResponseDto): Partial<RectConfig> {
    return this.isSeatSelectable(seat)
      ? {
          fill: undefined,
          stroke: seat?.seatContingent?.color,
          strokeWidth: seat?.seatContingent?.color ? 1 : 0,
        }
      : {
          fill: undefined,
          strokeWidth: 0,
        };
  }

  getCategorySeatShapeColor(seat: SalesPlanSeatResponseDto): Partial<RectConfig> {
    return this.isSeatSelectable(seat)
      ? {
          fill: this.getFillColor(seat?.seatCategory),
          stroke: undefined,
          strokeWidth: 0,
        }
      : {
          fill: ChartsUtils.getColorByPropertyName('--color-seat-disabled'),
          strokeWidth: undefined,
        };
  }

  private drawSeatBox(seat: SalesPlanSeatResponseDto) {
    switch (seat?.typeSeat) {
      case SalesSeatTypeEnumResponseDto.Normal:
        return this.shapesMap?.rect.clone({
          id: `seat-box-${seat?.physicalSeatId}`,
          name: `seat-box-${seat?.physicalSeatId}`,
          fill: ChartsUtils.getColorByPropertyName('--color-seat-disabled'),
          strokeWidth: 0,
        });
      case SalesSeatTypeEnumResponseDto.FoldingSeat:
        return this.shapesMap?.circle.clone({
          id: `seat-box-${seat?.physicalSeatId}`,
          name: `seat-box-${seat?.physicalSeatId}`,
          fill: ChartsUtils.getColorByPropertyName('--color-seat-disabled'),
          strokeWidth: 0,
        });
    }
  }

  private drawSeatNumber(seat: SalesPlanSeatResponseDto) {
    const seatNumber: Konva.Label = this.shapesMap?.number.clone({
      x: 0,
      y: this.seatSize + 1,
      id: `sales-seat-number-${seat?.physicalSeatId}`,
    });
    seatNumber.getText().text(`${seat?.seatNumber}`);
    const textWidth = seatNumber.getWidth();
    seatNumber.setAttr('x', (this.seatSize - textWidth) / 2);
    return seatNumber;
  }

  private drawSeatHighlighter(seat: SalesPlanSeatResponseDto) {
    switch (seat?.typeSeat) {
      case SalesSeatTypeEnumResponseDto.Normal:
        return this.shapesMap?.selectionHighlighter.clone({
          id: `sales-seat-highlighter-${seat?.physicalSeatId}`,
          cornerRadius: 2,
        });
      case SalesSeatTypeEnumResponseDto.FoldingSeat:
        return this.shapesMap?.selectionHighlighter.clone({
          id: `sales-seat-highlighter-${seat?.physicalSeatId}`,
          cornerRadius: this.seatSize,
        });
    }
  }

  public drawSeatIcon(seat: SalesPlanSeatResponseDto, image: HTMLImageElement) {
    return new Konva.Image({
      id: `seat-image-${seat?.physicalSeatId}`,
      image,
      x: 1,
      y: 1,
      width: this.seatSize - 2,
      height: this.seatSize - 2,
      perfectDrawEnabled: false,
      listening: false,
    });
  }

  public async appendIconToSeat(seat: SalesPlanSeatResponseDto, group: Konva.Group) {
    if (seat?.seatIsBanned) {
      group.add(this.drawSeatIcon(seat, this.iconsMap?.blockedSeatImage));
    } else if (this.seatHasImage(seat)) {
      group.add(this.drawSeatIcon(seat, this.iconsMap?.[seat?.seatContingent?.id]));
    }
  }

  public drawLetter(
    letter: EventSessionPlanLetterResponseDto,
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
  ) {
    const text = new Konva.Text({
      x: scaleX(letter?.x),
      y: scaleY(letter?.y),
      text: letter?.label,
      fill: letter?.color,
      fontFamily: letter?.font,
      fontSize: letter?.fontSize,
      id: `${letter?.label}`,
      align: 'left',
      perfectDrawEnabled: false,
      listening: false,
    });

    if (letter?.rotation) {
      this.rotateAroundCenter(text, letter);
    }
    return text;
  }

  public drawImage(
    props: { width: number; height: number; x?: number; y?: number; rotation?: number },
    image: HTMLImageElement,
    scaleX: (position: number) => number,
    scaleY: (position: number) => number,
  ) {
    return new Konva.Image({
      image,
      x: scaleX(props?.x),
      y: scaleY(props?.y),
      width: props?.width,
      height: props?.height,
      rotation: props?.rotation,
      perfectDrawEnabled: false,
      listening: false,
    });
  }

  public drawSearchResultIcons(result: SeatsBlockGroupResponseDto[], selectionLayer: Konva.Layer) {
    // removes the previous search result icons
    if (selectionLayer?.children) {
      selectionLayer.children
        .filter((child) => child.id().includes('search-result-mark'))
        .forEach((searchResultMark) => searchResultMark.destroy());
      result
        .filter((block) => block.blockId === this.eventData?.blockId)
        .reduce((acc, curr) => {
          if (curr?.seats) {
            return [...acc, ...curr.seats];
          }
          return acc;
        }, [])
        .forEach(({ seatId }, index) => {
          const node = this.getNotFreeSeatById(seatId) ?? this.getSeatById(seatId);
          if (node) {
            Konva.Image.fromURL(SearchResultIcon, (imageNode) => {
              imageNode.setAttrs({
                id: `search-result-mark-${index}`,
                x: node.x() - 2,
                y: node.y() - 10,
                width: this.seatSize + 4,
                height: this.seatSize + 4,
                rotation: node.rotation(),
              });
              selectionLayer.add(imageNode);
            });
          }
        });
      selectionLayer.batchDraw();
    }
  }

  public drawSelectionTooltip(initialText: string) {
    return new Konva.Label({
      x: 0,
      y: 0,
      visible: false,
    }).add(
      new Konva.Tag({
        fill: ChartsUtils.getColorByPropertyName('--color-black'),
        pointerWidth: 0,
        pointerHeight: 0,
        lineJoin: 'round',
        shadowColor: 'black',
        shadowBlur: 3,
        shadowOffsetX: 1,
        shadowOffsetY: 1,
        shadowOpacity: 0.5,
        cornerRadius: 2,
      }),
      new Konva.Text({
        id: 'selection-ttp-text',
        text: initialText,
        fontFamily: 'Roboto',
        fontSize: 14,
        padding: 4,
        fill: ChartsUtils.getColorByPropertyName('--color-white'),
      }),
    );
  }

  public getSeatConfig(
    seat: SalesPlanSeatResponseDto,
    includeFill: boolean,
    ignoreDisableCondition?: boolean,
  ): Partial<RectConfig> {
    if (!this.isSeatSelectable(seat) && !ignoreDisableCondition) {
      return {
        fill: ChartsUtils.getColorByPropertyName('--color-seat-disabled'),
        strokeWidth: 0,
      };
    }

    const strokeColor = seat?.seatContingent?.color;
    const config: Partial<RectConfig> = {
      stroke: strokeColor,
      strokeWidth: strokeColor ? 1 : 0,
    };

    if (includeFill) {
      config.fill = seat?.seatCategory?.color;
    }

    return config;
  }

  public isSeatSelectable(seat: SalesPlanSeatResponseDto) {
    return !seat?.seatIsBanned && seat?.seatIsAvailable && this.isStatusSelectable(seat);
  }

  private isStatusSelectable(seat: SalesPlanSeatResponseDto) {
    return seat.status === SalesSeatStatusEnumResponseDto.Free;
  }

  /**
   * used when the tooltip displays the number of seats included in a selection
   * more performant than isSeatSelectable
   */
  public isSeatSelectableByShape(group: Konva.Group) {
    return (
      // conflict between sonar & tsc
      // @ts-ignore
      group.findOne((child) => child instanceof Konva.Rect)?.fill() !==
      ChartsUtils.getColorByPropertyName('--color-seat-disabled')
    );
  }

  private getFillColor(category: SalesSeatCategoryResponseDto) {
    return category?.color || ChartsUtils.getColorByPropertyName('--color-seat-default');
  }

  public seatHasImage(seat: SalesPlanSeatResponseDto) {
    return !!this.eventData?.contingents.find((r) => r?.id === seat?.seatContingent?.id)?.image;
  }

  public applyParentGroupCache(parentGroupId: string, pixelRatio: number) {
    this.parentGroupMap?.[parentGroupId]?.cache({ pixelRatio });
  }

  public getSeatById(physicalSeatId: number) {
    return this.seatNodeMap?.[`${physicalSeatId}`];
  }

  public getNotFreeSeatById(physicalSeatId: number) {
    return this.notFreeSeatNodeMap?.[`${physicalSeatId}`];
  }

  public getSeatByPosition(node: Konva.Stage | Konva.Shape): {
    data: SalesPlanSeatResponseDto;
    node?: Konva.Stage | Konva.Group;
    pointerPosition?: Vector2d;
  } {
    if (node) {
      if (node instanceof Konva.Group) {
        const { x, y } = node.getAbsolutePosition();
        const pointerPosition = { x: x + node.width(), y };
        const physicalSeatId = node.id().slice('sales-seat-group-'.length);
        const data = this.eventData?.seats.find((element) => `${element?.physicalSeatId}` === physicalSeatId);
        return {
          node,
          pointerPosition,
          data,
        };
      }
      return this.getSeatByPosition(node.getParent());
    }
    return {
      data: null,
    };
  }

  public selectOneSeat(seat: EventSeatSelection) {
    if (this.isSeatSelectable(seat?.data)) {
      return { ...seat, seatId: seat?.data?.physicalSeatId, physicalSeatId: seat?.data?.physicalSeatId };
    }
  }

  public highlightSelectedNodes(selection: EventSeatSelection[], seatLayer: Konva.Layer) {
    selection.forEach(({ node, physicalSeatId, data }) => {
      if (node) {
        this.setGroupActiveState(node, physicalSeatId, data?.typeSeat, seatLayer);
      }
    });
  }

  public restoreSelectedNodes(
    selection: EventSeatSelection[],
    seatLayer: Konva.Layer,
    contingentLayer: Konva.Layer,
  ) {
    // restore seat box dimensions
    selection.forEach(({ node, physicalSeatId, data }) => {
      this.setGroupDefaultState(node, physicalSeatId, data?.typeSeat, seatLayer);
    });
  }

  private setGroupActiveState(
    group: Konva.Group,
    physicalSeatId: number,
    seatType: SalesSeatTypeEnumResponseDto,
    seatLayer: Konva.Layer,
  ) {
    if (group) {
      const seatBox: Konva.Rect = group.findOne(`#sales-seat-box-${physicalSeatId}`);
      const seatNumber: Konva.Label = seatLayer.findOne(`#sales-seat-number-${physicalSeatId}`);
      const seatHighlighter: Konva.Label = group.findOne(`#sales-seat-highlighter-${physicalSeatId}`);

      if (seatBox) {
        if (seatNumber) {
          seatNumber.getTag().setAttrs({
            fill: ChartsUtils.getColorByPropertyName('--color-primary-600'),
          });
        }
        if (seatHighlighter) {
          seatHighlighter.setAttrs({
            visible: true,
            name: 'highlighter-selected',
          });
        }
        seatBox.setAttrs({
          width: this.seatSize - 2.5,
          height: this.seatSize - 2.5,
          x: 1.25,
          y: 1.25,
          cornerRadius:
            seatType === SalesSeatTypeEnumResponseDto.FoldingSeat ? (seatBox.cornerRadius() as number) : 1,
        });
      }
    }
  }

  private setGroupDefaultState(
    group: Konva.Group,
    physicalSeatId: number,
    seatType: SalesSeatTypeEnumResponseDto,
    seatLayer: Konva.Layer,
  ) {
    if (group) {
      const seatBox: Konva.Rect = group.findOne(`#sales-seat-box-${physicalSeatId}`);
      const seatNumber: Konva.Label = seatLayer.findOne(`#sales-seat-number-${physicalSeatId}`);
      const seatHighlighter: Konva.Label = group.findOne(`#sales-seat-highlighter-${physicalSeatId}`);
      if (seatBox) {
        if (seatNumber) {
          seatNumber.getTag().setAttrs({
            fill: 'transparent',
          });
        }
        if (seatHighlighter) {
          seatHighlighter.name('');

          seatHighlighter.setAttrs({
            visible: false,
            name: 'selection-highlighter',
          });
        }
        seatBox.setAttrs({
          width: this.seatSize,
          height: this.seatSize,
          x: 0,
          y: 0,
          cornerRadius:
            seatType === SalesSeatTypeEnumResponseDto.FoldingSeat ? (seatBox.cornerRadius() as number) : 2,
        });
      }
    }
  }

  /**
   * when doing multiple selection, this function will determine the intersection between the stage
   * and the selection shape (rect or lasso)
   * and checks if there are seats included in this intersection
   */
  public getAreaAndStageIntersection(contingentLayer: Konva.Layer, selectionArea: Konva.Rect | Konva.Line) {
    const selectionAreaPoints = this.getShapePoints(selectionArea);
    return this.getAllSeatGroups(contingentLayer).filter(
      (group: Konva.Group) =>
        this.isShapeWithinArea(this.getShapePoints(group), selectionAreaPoints) &&
        this.isSeatSelectableByShape(group),
    );
  }

  /**
   * the condition that allows to select a seat. For now, a seat is selected only if there are a minimum of two
   * points included in the selection shape (rect or lasso)
   */
  private isShapeWithinArea(shapePoints: [number, number][], areaPoints: [number, number][]): boolean {
    return shapePoints?.filter((point) => polygonContains(areaPoints, point))?.length >= 2;
  }

  private getAllSeatGroups(contingentLayer: Konva.Layer) {
    return (contingentLayer.children || []).reduce(
      (acc: Konva.Group[], curr: Konva.Group) => [...acc, ...(curr.children || [])],
      [],
    );
  }

  public selectSeats(contingentLayer: Konva.Layer, selectionArea: Konva.Rect | Konva.Line) {
    let selectionClone = [];
    this.getAreaAndStageIntersection(contingentLayer, selectionArea)
      .map((group: Konva.Group) =>
        this.getSeatByPosition(group.findOne((shape) => shape instanceof Konva.Shape)),
      )
      .forEach(({ node, data }) => {
        if (data) {
          const selectedSeat = this.selectOneSeat({ node, data });
          if (selectedSeat) {
            selectionClone = [...selectionClone, selectedSeat];
          }
        }
      });

    return selectionClone;
  }

  private getShapePoints(shape: Konva.Line | Konva.Rect | Konva.Group): [number, number][] {
    if (!shape) {
      return [];
    }
    // lasso
    if (shape instanceof Konva.Line) {
      const points = shape.points();
      const result: [number, number][] = [];
      let i = 0;
      while (i < points?.length - 1) {
        result.push([points[i], points[i + 1]]);
        i += 2;
      }
      return result;
      // rect
    } else {
      const { x, y } = shape.getPosition();
      return [
        [x, y],
        [x + shape.width(), y],
        [x + shape.width(), y + shape.height()],
        [x, y + shape.height()],
      ];
    }
  }

  private rotatePoint({ x, y }, rotation: number) {
    const angleRadians = (rotation * Math.PI) / 180;
    const rcos = Math.cos(angleRadians);
    const rsin = Math.sin(angleRadians);
    return { x: x * rcos - y * rsin, y: y * rcos + x * rsin };
  }

  private rotateAroundCenter(node: Konva.Text, letter: EventSessionPlanLetterResponseDto) {
    const topLeft = { x: (-node.width() * 3) / 5, y: (-node.height() * 3) / 5 };
    const current = this.rotatePoint(topLeft, 0);
    const rotated = this.rotatePoint(topLeft, letter?.rotation);
    const dx = rotated.x - current.x,
      dy = rotated.y - current.y;

    node.rotation(letter?.rotation);
    node.x(node.x() + dx + (16 * (letter?.label?.length - 1)) / 3);
    node.y(node.y() + dy + (16 * (letter?.label?.length - 1)) / 3);
  }

  public getZoomProps(
    stage: Konva.Stage,
    wheelEvent: Konva.KonvaEventObject<WheelEvent>,
    scaleBy: number,
    initialScale: number,
    zoomDirection?: number,
  ): { oldScale: number; newScale: number; position: Vector2d } {
    if (wheelEvent) {
      // stop default scrolling
      wheelEvent.evt.preventDefault();
    }
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };
    // how to scale? Zoom in? Or zoom out?
    let direction = zoomDirection || (wheelEvent?.evt?.deltaY > 0 ? -1 : 1);
    // when we zoom on trackpad, evt.ctrlKey is true
    // in that case lets revert direction
    if (wheelEvent?.evt?.ctrlKey) {
      direction = -direction;
    }
    let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;
    // round it to 3 decimals
    newScale = Math.round(newScale * 1000) / 1000;
    const position =
      newScale === initialScale
        ? { x: 0, y: 0 }
        : {
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale,
          };

    return { oldScale, newScale, position };
  }

  public getPointerPosition(stage: Konva.Stage): Vector2d {
    const pointer = stage.getPointerPosition();
    return {
      x: (pointer.x - stage.x()) / stage.scaleX(),
      y: (pointer.y - stage.y()) / stage.scaleY(),
    };
  }

  /**
   * used to load images only once, and caching them for repetitive use
   */
  private async loadReusableAssets() {
    // blocked seat icon
    this.iconsMap.blockedSeatImage = await this.loadAsset(BlockedSeatIcon);
    if (this.eventData?.contingents) {
      await Promise.all(
        this.eventData.contingents
          .filter(({ image }) => !!image)
          .map(async ({ image, id }) => {
            this.iconsMap[id] = await this.loadAsset(`data:image/png;base64,${image}`);
          }),
      );
    }
  }

  /**
   * transforms a base64 image to a canvas element
   */
  private loadAsset(imageSrc: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageSrc;
    });
  }

  public getInitialScale(container: ElementRef, props: CanvasProps) {
    // now we need to fit stage into parent container
    const containerWidth = container?.nativeElement?.offsetWidth;
    // to do this we need to scale the stage
    const initialScaleX = containerWidth / (props?.width + 16);
    // now we need to fit stage into parent
    const containerHeight = container?.nativeElement?.offsetHeight;
    // to do this we need to scale the stage
    const initialScaleY = containerHeight / (props?.height + 16);
    return Math.round(Math.min(initialScaleX, initialScaleY) * 1000) / 1000;
  }

  /**
   * limits the drag event on the stage
   */
  public defineDragBoundaries({ x, y }): Vector2d {
    let newY = y;
    let newX = x;
    // left boundaries
    if (newX > 200) {
      newX = 200;
    }
    // top boundaries
    if (newY > 200) {
      newY = 200;
    }
    return {
      x: newX,
      y: newY,
    };
  }

  /**
   * calculates the natural width and height of the plan,
   * based on all the elements coordinates (seats, letters, images)
   */
  public getCanvasProps(elements: { x: number; y: number; [key: string]: any }[]): CanvasProps {
    const maxX = _.max(elements, (element) => element?.x) as any;
    const minX = _.min(elements, (element) => element?.x) as any;
    const maxY = _.max(elements, (element) => element?.y) as any;
    const minY = _.min(elements, (element) => element?.y) as any;
    return {
      width: maxX?.x - minX?.x,
      height: maxY?.y - minY?.y,
      maxX: maxX?.x,
      maxY: maxY?.y,
      minX: minX?.x,
      minY: minY?.y,
    };
  }

  /**
   * getting all the plan elements is useful to calculate the canvas natural dimensions and scaling
   */
  public getAllPlanElements() {
    return [
      ...(this.eventData?.seats
        ? this.eventData.seats.map((seat) => ({
            ...seat,
            x: seat?.positionX,
            y: seat?.positionY,
          }))
        : []),
      ...(this.eventData?.letters ? this.eventData.letters : []),
      ...(this.eventData?.imageData?.imageDatas ?? []).map((image) => ({
        ...image,
        x: (image?.x ?? 0) + (image?.width ?? 0),
        y: (image?.y ?? 0) + (image?.height ?? 0),
      })),
      ...(this.eventData?.shapes
        ? this.eventData.shapes.map((shape) => ({
            ...shape,
            x: shape?.positionX,
            y: shape?.positionY,
          }))
        : []),
    ];
  }

  /**
   * caches the redundant element shapes (rectangle, circle, seat number, etc..)
   * contains the common attributes between elements. when we begin drawing, we only specify:
   * color, stroke and coordinates
   */
  private async prepareShapeCache() {
    this.shapesMap = {
      rect: new Konva.Rect({
        width: this.seatSize,
        height: this.seatSize,
        cornerRadius: 2,
        perfectDrawEnabled: false,
        shadowForStrokeEnabled: false,
        hitStrokeWidth: 0,
      }),
      circle: new Konva.Rect({
        width: this.seatSize,
        height: this.seatSize,
        cornerRadius: this.seatSize,
        perfectDrawEnabled: false,
        shadowForStrokeEnabled: false,
        hitStrokeWidth: 0,
      }),
      number: new Konva.Label({
        x: 0,
        y: 2,
        listening: false,
      }).add(
        new Konva.Tag({
          fill: 'transparent',
          pointerWidth: 0,
          pointerHeight: 0,
          lineJoin: 'round',
          shadowForStrokeEnabled: false,
          cornerRadius: 2,
          listening: false,
        }),
        new Konva.Text({
          text: null,
          fontFamily: 'Roboto',
          fontStyle: 'bold',
          fontSize: 6,
          verticalAlign: 'middle',
          align: 'center',
          hitStrokeWidth: 0,
          shadowForStrokeEnabled: false,
          fill: ChartsUtils.getColorByPropertyName('--color-black'),
          listening: false,
        }),
      ),
      selectionHighlighter: new Konva.Rect({
        name: 'selection-highlighter',
        width: this.seatSize,
        height: this.seatSize,
        fill: 'transparent',
        stroke: ChartsUtils.getColorByPropertyName('--color-primary-600'),
        strokeWidth: 1,
        perfectDrawEnabled: false,
        shadowForStrokeEnabled: false,
        hitStrokeWidth: 0,
        visible: false,
        listening: false,
      }),
    };
    await this.loadReusableAssets();
  }
}
